import type { Poi } from '@workspaces/types'
import markerLogoUrl from '@/assets/icons/markers/marker-logo.svg'
import { loadImageElement } from '@/services/poi-icons.service'

/**
 * POI pin compositor (sc-563940): draws an uploaded logo inside the standard
 * marker balloon (assets/icons/markers/marker-logo.svg), producing the exact
 * bitmap both the map layers and the modal's fit previews display — so what
 * the user picks in the modal is what the map renders.
 *
 * `whole` keeps the complete logo visible inside the pin head (contain);
 * `crop` fills the head and crops the overflow to its circle (cover). In both
 * fits the logo sits on a white disc that covers the marker's inner circle.
 */

/**
 * marker-logo.svg geometry (44x44 viewBox): inner circle centered at (22, 20)
 * with radius 18, tail tip at (22, 42). The canvas renders it at 3x so logos
 * stay crisp at the on-map maximum (54px) and in the modal previews.
 */
const MARKER_SVG_SIZE = 44
const CANVAS_SCALE = 3
const PIN_SIZE = MARKER_SVG_SIZE * CANVAS_SCALE
const PIN_HEAD_CENTER_X = 22 * CANVAS_SCALE
const PIN_HEAD_CENTER_Y = 20 * CANVAS_SCALE
const PIN_HEAD_RADIUS = 18 * CANVAS_SCALE
const PIN_TIP_Y = 42 * CANVAS_SCALE

const LOGO_BACKGROUND_COLOR = '#ffffff'

/**
 * White halo the background disc keeps visible between the logo and the
 * marker's inner circle edge, in rendered pixels. Applies to both fits:
 * `crop` shrinks its clip circle by it and `whole` fits the logo inside the
 * halo'd head.
 */
const LOGO_HALO_RENDERED_PX = 2
const LOGO_HALO_PX = LOGO_HALO_RENDERED_PX * CANVAS_SCALE
const LOGO_CLIP_RADIUS = PIN_HEAD_RADIUS - LOGO_HALO_PX

/**
 * Side of the square the `whole` fit fits the logo into: the square inscribed
 * in the halo'd head circle, so even a square logo's corners keep the halo
 * distance from the circle edge.
 */
const WHOLE_LOGO_BOX_SIDE = LOGO_CLIP_RADIUS * Math.SQRT2

export interface ComposedPoiPinIcon {
  /** PNG data URL of the composed pin. */
  url: string
  width: number
  height: number
  /** Anchor at the pin tip, so the marker points at the POI location. */
  anchorY: number
}

/** Drawable sources with numeric intrinsic dimensions. */
export type PinImageSource = HTMLImageElement | HTMLCanvasElement | ImageBitmap

export type PinImageLoader = (url: string) => Promise<PinImageSource>

/**
 * Loaded images, cached forever by URL (they are immutable by design): the
 * balloon asset loads once ever, and a logo loads once for both fits.
 * `anonymous` because the composed canvas would otherwise be tainted.
 */
const loadedImages = new Map<string, Promise<PinImageSource>>()

function loadPinImage(url: string): Promise<PinImageSource> {
  let image = loadedImages.get(url)
  if (!image) {
    image = loadImageElement(url, 'anonymous')
    // A failed load is evicted so a later retry can succeed.
    image.catch(() => loadedImages.delete(url))
    loadedImages.set(url, image)
  }
  return image
}

/** The marker balloon plus the white disc the logo sits on. */
function drawPinBase(
  context: CanvasRenderingContext2D,
  balloon: PinImageSource,
): void {
  context.drawImage(balloon, 0, 0, PIN_SIZE, PIN_SIZE)
  context.beginPath()
  context.arc(
    PIN_HEAD_CENTER_X,
    PIN_HEAD_CENTER_Y,
    PIN_HEAD_RADIUS,
    0,
    Math.PI * 2,
  )
  context.fillStyle = LOGO_BACKGROUND_COLOR
  context.fill()
}

function drawWholeLogo(
  context: CanvasRenderingContext2D,
  image: PinImageSource,
): void {
  const { width, height } = image
  const scale = Math.min(
    WHOLE_LOGO_BOX_SIDE / width,
    WHOLE_LOGO_BOX_SIDE / height,
  )
  const drawWidth = width * scale
  const drawHeight = height * scale
  context.drawImage(
    image,
    PIN_HEAD_CENTER_X - drawWidth / 2,
    PIN_HEAD_CENTER_Y - drawHeight / 2,
    drawWidth,
    drawHeight,
  )
}

function drawCroppedLogo(
  context: CanvasRenderingContext2D,
  image: PinImageSource,
): void {
  const { width, height } = image
  const diameter = LOGO_CLIP_RADIUS * 2
  const scale = Math.max(diameter / width, diameter / height)
  const drawWidth = width * scale
  const drawHeight = height * scale

  context.save()
  context.beginPath()
  context.arc(
    PIN_HEAD_CENTER_X,
    PIN_HEAD_CENTER_Y,
    LOGO_CLIP_RADIUS,
    0,
    Math.PI * 2,
  )
  context.clip()
  context.drawImage(
    image,
    PIN_HEAD_CENTER_X - drawWidth / 2,
    PIN_HEAD_CENTER_Y - drawHeight / 2,
    drawWidth,
    drawHeight,
  )
  context.restore()
}

function composeOnCanvas(
  balloon: PinImageSource,
  image: PinImageSource,
  fit: Poi.PoiIconFit,
): ComposedPoiPinIcon {
  const canvas = document.createElement('canvas')
  canvas.width = PIN_SIZE
  canvas.height = PIN_SIZE

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Canvas 2D context is unavailable')
  }

  drawPinBase(context, balloon)
  if (fit === 'crop') {
    drawCroppedLogo(context, image)
  } else {
    drawWholeLogo(context, image)
  }

  return {
    url: canvas.toDataURL('image/png'),
    width: PIN_SIZE,
    height: PIN_SIZE,
    anchorY: PIN_TIP_Y,
  }
}

/**
 * Composed pins, cached forever by (logo URL, fit): logo URLs are immutable
 * by design (a replacement uploads a new basename), so a pair never has to be
 * re-drawn. The promise itself is cached so concurrent callers (the map cache
 * and the modal previews) share one composition.
 */
const composedPinsCache = new Map<string, Promise<ComposedPoiPinIcon>>()

export async function composePoiPinIcon(
  logoUrl: string,
  fit: Poi.PoiIconFit,
  imageLoader: PinImageLoader = loadPinImage,
): Promise<ComposedPoiPinIcon> {
  const cacheKey = `${logoUrl}#${fit}`
  let composed = composedPinsCache.get(cacheKey)
  if (!composed) {
    composed = Promise.all([
      imageLoader(markerLogoUrl),
      imageLoader(logoUrl),
    ]).then(([balloon, image]) => composeOnCanvas(balloon, image, fit))
    // A failed load/compose is evicted so a later retry can succeed.
    composed.catch(() => composedPinsCache.delete(cacheKey))
    composedPinsCache.set(cacheKey, composed)
  }
  return composed
}
